import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import NewMemberWelcomeSettings from '../../components/admin/NewMemberWelcomeSettings';
import TagBubbles from '../../components/admin/TagBubbles';
import api from '../../services/api';
import * as sequenceContent from '../../services/adminSequenceContent.service';
import * as adminHubAnnouncement from '../../services/adminHubAnnouncement.service';
import { renderRichText } from '../../utils/richText';
import RichTextEditor from '../../components/common/RichTextEditor';
import { htmlToMarkers } from '../../utils/richTextEditor';

// One page, one shared set of controls (one-off entry, bulk create, paste,
// CSV failsafe) for every place Team365 pushes content — the destination
// is just a choice at the top, not a different screen or a different way
// of working. Onboarding/daily/renewal write into email_sequence_content
// (day-indexed, nothing sends yet); Announcements writes into the same
// hub_announcements table the live Hive "Thought for the Day" reads from
// (a rolling feed, day number used only to order a bulk load, not stored).
const DESTINATIONS = [
  { value: 'onboarding',    label: 'Onboarding',         isAnnouncement: false },
  { value: 'daily',         label: 'Daily',               isAnnouncement: false },
  { value: 'renewal',       label: 'Renewal',             isAnnouncement: false },
  { value: 'announcements', label: 'Announcements (Hive)', isAnnouncement: true },
  { value: 'quiz_phoenix',  label: 'Quiz · Rising Phoenix',    isAnnouncement: false },
  { value: 'quiz_explorer', label: 'Quiz · Evolving Explorer', isAnnouncement: false },
  { value: 'quiz_glower',   label: "Quiz · Grounded Glow'er",  isAnnouncement: false },
  { value: 'trial',         label: 'Trial',                     isAnnouncement: false },
];

// Renewal emails (2026-10-07): the day number picks the moment in a
// member's year, and the title is the subject line members see — so the
// slot is named here for Team365, never in the title. In the text,
// {{date}} = the day their year ends, {{price}} = " at $20.58 a month" (or
// nothing), {{accomplishments}} = their own bullet list. Button links can
// be renew:auto_renew_on, renew:continue or renew:restart_daily — each
// becomes a one-tap link for that member.
export const RENEWAL_SLOTS = {
  60: '60 days before · auto-renew off', 61: '60 days before · auto-renew on',
  45: '45 days before · auto-renew off',
  30: '30 days before · auto-renew off', 31: '30 days before · auto-renew on',
  15: '15 days before · auto-renew off', 1: '1 day before · auto-renew off',
  62: '60 days before · royalty (renewal covered by 365reset)',
  366: 'Day 1 of their new year',
};
const isQuiz = (destination) => destination.startsWith('quiz_');
// Trial emails (2026-10-13): the day number picks the moment, like renewal.
// In the words, {{first_name}}, {{date}} (their trial end) and {{price}}
// (what they'll pay after, e.g. "CA$20.58 a month") are filled in.
export const TRIAL_SLOTS = {
  5: 'Day 5 of their trial', 1: 'Day before their trial ends',
  100: 'Became a full member (first real payment)', 999: 'Goodbye (trial ended without joining)',
};
const dayLabel = (destination, n) =>
  destination === 'renewal' && RENEWAL_SLOTS[n] != null ? RENEWAL_SLOTS[n]
    : destination === 'trial' && TRIAL_SLOTS[n] != null ? TRIAL_SLOTS[n]
    : isQuiz(destination) ? `Day ${n} after the quiz` : `Day ${n}`;

/** "Label | URL" per line, matching the CSV/course-lesson convention. */
const linksToText = (links) => (links || []).map(l => l.title ? `${l.title} | ${l.url}` : l.url).join('\n');
const textToLinks = (text) => (text || '').split('\n').map(s => s.trim()).filter(Boolean).map(line => {
  const [maybeTitle, maybeUrl] = line.split('|').map(s => s.trim());
  return maybeUrl ? { title: maybeTitle, url: maybeUrl } : { title: '', url: maybeTitle };
});

// ─── Paste a range copied from Google Sheets / Excel straight into bulk rows —
// column order matches Jenn's real spreadsheet (Fits with Days Content, Title,
// Content, Image, Resources). When the browser gives us the rich HTML version
// of the clipboard (which Sheets/Excel always include alongside plain text),
// each cell is run through the same DOM serializer the rich text editor uses,
// so real Bold/Italic applied in the sheet survives the paste as real
// formatting — not just the text. ───────────────────────────────────────────

// She should never have to think about "columns" or "mapping" — paste and
// it works, full stop. So instead of asking her anything, each column is
// identified by what the actual pasted values look like: the column of
// plain numbers is the day, the column of links is the image, the column
// with by far the longest text is the content. That covers the three
// fields that actually make or break a day's content. Title and alt text
// are lower-confidence to guess this way, so they're just left for her to
// glance at per-row afterward — same as always, not a new step.

/**
 * Parses the clipboard's HTML table (if present) into a 2D array of
 * marker-formatted cell strings. Uses htmlToMarkers (not plain
 * serializeInline) per cell so a cell with multiple paragraphs — common
 * for a body-copy column — keeps its real paragraph breaks instead of
 * every line getting glued together with no separation at all.
 */
const parseHtmlGrid = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const table = doc.querySelector('table');
  if (!table) return null;
  return Array.from(table.querySelectorAll('tr')).map(tr =>
    Array.from(tr.querySelectorAll('td,th')).map(cell => htmlToMarkers(cell).trim())
  );
};

/**
 * Plain-text clipboard fallback (no rich HTML available) — tab-separated,
 * newline-separated, TSV-quoted per the same convention every spreadsheet
 * app uses for a cell whose own content contains a tab or a line break: the
 * whole field is wrapped in "..." and a literal quote inside it is doubled
 * ("" becomes a single "). Jenn, 2026-09-05: a real multi-paragraph body
 * cell (blank lines between paragraphs, itself wrapped in quotes) was
 * getting shredded into one garbage fragment row per blank line, because
 * the previous version just split on every line break with no idea a
 * quoted field is allowed to contain its own. Confirmed directly against
 * her real pasted content — a single row exploded into 9.
 */
const parsePlainGrid = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"' && field === '') { inQuotes = true; i++; continue; }
    if (c === '\t') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') { i++; continue; } // normalize CRLF — the \n right after closes the row
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c; i++;
  }
  row.push(field);
  if (row.length > 1 || row[0] !== '') rows.push(row);
  return rows.filter(r => r.some(cell => cell.trim().length > 0));
};

// ─── What's empty — mirrors server/src/utils/contentProblems.js exactly, so
// the red warnings before Save and the red list after an upload can never
// disagree about what "incomplete" means. Jenn, 2026-09-24: "there's nothing
// to alert me when a field is empty" — so the page does the watching. ─────
const problemsFor = ({ isAnnouncement, title, body, imageUrl, imageAltText }) => {
  const problems = [];
  if (!(body || '').trim()) problems.push(isAnnouncement ? 'no message' : 'no email text');
  if (!(title || '').trim()) problems.push(isAnnouncement ? 'no title' : 'no subject line');
  if ((imageUrl || '').trim() && !(imageAltText || '').trim()) problems.push('picture has no description');
  return problems;
};

// ─── Heading row -> columns by name. Same names the CSV imports recognize
// (emailSequenceContent/hubAnnouncement normalizeCsvRow), so a block copied
// from the Content Bank sheet WITH its heading row lands exactly the way the
// same sheet does as a CSV. Jenn, 2026-09-24: the guessing paste "often drops
// titles, and particularly misses fields in the announcements part". The
// combined sheet has both Email and Hub columns side by side, so which title
// and body columns count depends on the destination. ───────────────────────
const HEADING_NAMES = {
  dayNumber: ['fits with days content', 'day', 'day number', 'day #'],
  bodyPart2: ['content 2', 'body 2', 'content part 2', 'email body part 2'],
  imageUrl: ['image', 'image url', 'image link'],
  imageAltText: ['image alt', 'image alt text', 'alt text'],
  resourceLinksText: ['resources', 'resource links'],
};
const TITLE_NAMES = { email: ['title', 'email subject'], announcement: ['title', 'hub title'] };
const BODY_NAMES = { email: ['content', 'body', 'email body part 1'], announcement: ['content', 'body', 'hub body'] };
const ALL_HEADING_NAMES = new Set([
  ...Object.values(HEADING_NAMES).flat(), ...TITLE_NAMES.email, ...TITLE_NAMES.announcement, ...BODY_NAMES.email, ...BODY_NAMES.announcement,
]);

// A bold heading comes through the rich paste as "**Day**" — strip the markers before comparing.
// Notes in brackets ("Resource Links (Label | URL)") and a trailing ":" or "." are ignored too.
const normalizeHeading = (cell) => (cell || '').replace(/\([^)]*\)/g, ' ').replace(/[*_]/g, '').trim().toLowerCase()
  .replace(/\s+/g, ' ').replace(/[:.]+$/, '').trim();
const isHeadingRow = (cells) => cells.filter(c => ALL_HEADING_NAMES.has(normalizeHeading(c))).length >= 2;

const columnsFromHeadings = (cells, isAnnouncement) => {
  const names = cells.map(normalizeHeading);
  const find = (aliases) => { const i = names.findIndex(n => aliases.includes(n)); return i === -1 ? undefined : i; };
  const side = isAnnouncement ? 'announcement' : 'email';
  return {
    dayNumber: find(HEADING_NAMES.dayNumber),
    title: find(TITLE_NAMES[side]),
    body: find(BODY_NAMES[side]),
    bodyPart2: isAnnouncement ? undefined : find(HEADING_NAMES.bodyPart2),
    imageUrl: find(HEADING_NAMES.imageUrl),
    imageAltText: find(HEADING_NAMES.imageAltText),
    resourceLinksText: find(HEADING_NAMES.resourceLinksText),
  };
};

/**
 * Pasted grid -> { rows, leftOut, notice }. Reads columns by heading name when
 * the block includes a heading row; only guesses (guessColumns) when it doesn't.
 * leftOut lists pasted rows that had something in them but can't be saved, so
 * they're named in red instead of silently vanishing.
 */
const gridToBulkRows = (grid, isAnnouncement = false) => {
  if (!grid || grid.length === 0) return { rows: [], leftOut: [], notice: null };

  // Anything above the heading row (a banner, a phase label) is ignored, and so
  // is a repeat of the heading row further down (two blocks copied together).
  const headingIndex = grid.slice(0, 10).findIndex(isHeadingRow);
  let cols, dataRows;
  if (headingIndex !== -1) {
    cols = columnsFromHeadings(grid[headingIndex], isAnnouncement);
    dataRows = grid.slice(headingIndex + 1).filter(r => !isHeadingRow(r));
    if (cols.body === undefined) {
      const want = isAnnouncement ? '"Hub Body"' : '"Email Body Part 1"';
      return { rows: [], leftOut: [], notice: `Nothing was pasted: your headings have no ${want} column, so there's no text to put in these entries.` };
    }
  } else {
    cols = guessColumns(grid);
    dataRows = grid;
  }

  const cell = (row, colIndex) => (colIndex !== undefined ? (row[colIndex] || '').trim() : '');
  const rows = [];
  const leftOut = [];
  dataRows.forEach(cells => {
    const r = {
      key: Math.random().toString(36).slice(2),
      dayNumber: cell(cells, cols.dayNumber).replace(/[*_]/g, ''),
      title: cell(cells, cols.title),
      body: cell(cells, cols.body),
      bodyPart2: cell(cells, cols.bodyPart2),
      imageUrl: cell(cells, cols.imageUrl),
      imageAltText: cell(cells, cols.imageAltText),
      resourceLinksText: cell(cells, cols.resourceLinksText),
    };
    if ((isAnnouncement || r.dayNumber) && r.body) { rows.push(r); return; }
    leftOut.push({
      kind: isAnnouncement ? 'announcement' : 'daily',
      day: r.dayNumber || null,
      problems: ['NOT PASTED — ' + (!r.body ? (isAnnouncement ? 'no message' : 'no email text') : 'no day number')],
    });
  });
  return { rows, leftOut, notice: null };
};

/** No heading row: columns identified by what the values actually are, not by position.
 *  Day number is required for daily/onboarding/renewal content, but announcements are a rolling feed —
 *  a paste with no day-number column is normal for them, not something to silently reject. */
const guessColumns = (grid) => {
  const colCount = Math.max(...grid.map(r => r.length));

  const columns = Array.from({ length: colCount }, (_, i) => {
    const values = grid.map(row => (row[i] || '').trim()).filter(Boolean);
    const distinctCount = new Set(values).size;
    return {
      index: i,
      isAllNumeric: values.length > 0 && values.every(v => /^\d+$/.test(v)),
      isAllUrl: values.length > 0 && values.every(v => /^https?:\/\//i.test(v)),
      avgLength: values.length > 0 ? values.reduce((sum, v) => sum + v.length, 0) / values.length : 0,
      // A real body paragraph has its own internal blank-line breaks once
      // pasted (see parsePlainGrid's quote handling) — a caption or alt
      // text never does, no matter how long it happens to run. Jenn,
      // 2026-09-05: an image description that happened to be a long single
      // sentence (over the old length floor) got mistaken for a body
      // column and bumped the real ones out of position — length alone
      // isn't a safe signal, since a caption can occasionally run as long
      // as a short paragraph. Whether it has its OWN paragraph breaks is.
      hasParagraphBreak: values.some(v => /\n\s*\n/.test(v)),
      // A column repeating the same value across most rows (a phase/theme
      // label, not day-specific content) is never the title.
      variesPerRow: values.length === 0 || distinctCount > 1,
    };
  });

  const dayCol = columns.find(c => c.isAllNumeric)?.index;
  const imageCol = columns.find(c => c.isAllUrl)?.index;
  const remaining = columns.filter(c => c.index !== dayCol && c.index !== imageCol);
  // Which of the two body columns is "Part 1" vs "Part 2" is decided by
  // which comes FIRST in the actual sheet, never by which is longer —
  // Jenn, 2026-09-05, a real row where Part 2 happened to run longer than
  // Part 1 landed with the two swapped when this ranked them by length.
  const BODY_PART2_MIN_LENGTH = 100;
  const paragraphColumns = remaining.filter(c => c.hasParagraphBreak).sort((a, b) => a.index - b.index);
  const longColumns = paragraphColumns.length > 0
    ? paragraphColumns
    : remaining.filter(c => c.avgLength >= BODY_PART2_MIN_LENGTH).sort((a, b) => a.index - b.index);
  const bodyCol = longColumns.length > 0
    ? longColumns[0].index
    : (remaining.length > 0 ? remaining.reduce((longest, c) => (c.avgLength > longest.avgLength ? c : longest)).index : undefined);
  const bodyPart2Col = longColumns.length > 1 ? longColumns[1].index : undefined;
  // Of what's left, the title is the shortest column that still changes
  // day to day — captions/directions/subjects run longer than a real title.
  const titleCandidates = remaining.filter(c => c.index !== bodyCol && c.index !== bodyPart2Col && c.variesPerRow && c.avgLength > 0);
  const titleCol = titleCandidates.length > 0
    ? titleCandidates.reduce((shortest, c) => (c.avgLength < shortest.avgLength ? c : shortest)).index
    : undefined;

  // The image alt text is whichever remaining column is its OWN description,
  // not a quote or restatement of the day's content — a caption or subject
  // line will usually repeat a chunk of the body text; a real image
  // description won't. Checked against a whole row's worth of columns
  // rather than one, since a caption can coincidentally echo the body on
  // some rows but not others.
  const overlapsBody = (colIndex) => {
    if (colIndex === undefined) return 1;
    let overlapping = 0, checked = 0;
    grid.forEach(row => {
      const candidate = (row[colIndex] || '').trim();
      const bodyText = (row[bodyCol] || '').trim();
      if (candidate.length <= 10 || !bodyText) return;
      checked++;
      if (bodyText.includes(candidate.slice(0, 30)) || bodyText.includes(candidate.slice(-30))) overlapping++;
    });
    return checked > 0 ? overlapping / checked : 1;
  };
  const altCandidates = remaining.filter(c => c.index !== bodyCol && c.index !== bodyPart2Col && c.index !== titleCol && c.variesPerRow && c.avgLength > 0);
  const imageAltCol = altCandidates.length > 0
    ? altCandidates.reduce((best, c) => (overlapsBody(c.index) < overlapsBody(best.index) ? c : best)).index
    : undefined;

  return { dayNumber: dayCol, title: titleCol, body: bodyCol, bodyPart2: bodyPart2Col, imageUrl: imageCol, imageAltText: imageAltCol, resourceLinksText: undefined };
};

/**
 * The red list: every entry that came in (or is about to go in) with
 * something empty, one line each, in plain words. Used after an upload or a
 * bulk save (from the server's `incomplete` list) and for pasted rows that
 * were left out.
 */
function IncompleteList({ items, heading }) {
  if (!items || items.length === 0) return null;
  const sorted = [...items].sort((a, b) => (a.day ?? Infinity) - (b.day ?? Infinity));
  return (
    <div role="alert" className="mt-3 bg-red-50 border-2 border-red-300 text-red-800 rounded-xl p-4 text-sm">
      <p className="font-semibold mb-1.5">
        {heading || `${items.length} ${items.length === 1 ? 'entry has' : 'entries have'} something empty — please check:`}
      </p>
      <ul className="list-disc pl-5 space-y-0.5">
        {sorted.map((it, i) => (
          <li key={i}>
            <span className="font-medium">{it.label || (it.day != null ? `Day ${it.day}` : 'A row with no day number')}</span>
            {' · '}{it.kind === 'announcement' ? 'Thought for the Day' : 'Email'}: {it.problems.join(', ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** After an upload: the red list, or a plain "nothing came in empty" so silence never has to be interpreted. */
function UploadCheck({ incomplete }) {
  if (!incomplete) return null;
  if (incomplete.length === 0) return <p className="mt-2 text-xs font-medium text-reset-sage">✓ Nothing came in empty.</p>;
  return <IncompleteList items={incomplete} />;
}

/** Loads the topic tags for the list on screen (and every tag used, for the suggestions). Renders nothing. */
function TagLoader({ content, kind, setItemTags, setAllTags }) {
  const ids = content.map(c => c.id).join(',');
  useEffect(() => {
    let live = true;
    api.get('/admin/tags', { params: { kind } }).then(r => live && setAllTags(r.data.tags)).catch(() => {});
    if (ids) api.get('/admin/tags/items', { params: { kind, ids } }).then(r => live && setItemTags(r.data.tags)).catch(() => {});
    return () => { live = false; };
  }, [ids, kind, setItemTags, setAllTags]);
  return null;
}

export default function AdminSequenceContent() {
  const [destination, setDestination] = useState('daily');
  const [content, setContent] = useState(null);
  // Topic tags on every email and announcement (Jenn, 2026-10-13 + 2026-10-29); search uses them.
  const [itemTags, setItemTags] = useState({});
  const [allTags, setAllTags] = useState([]);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null = closed, 'new', or an item object
  const [saving, setSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);
  const [bulkCreating, setBulkCreating] = useState(false); // shows the multi-row native entry table
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileInputRef = useRef(null);
  const editFormRef = useRef(null);
  const combinedFileInputRef = useRef(null);
  const [combinedImporting, setCombinedImporting] = useState(false);
  const [combinedResult, setCombinedResult] = useState(null);

  // The edit form renders above the entry list, not next to whichever row
  // you clicked "Edit" on — with a long list (a full bulk-uploaded batch),
  // that row can be scrolled well below where the form actually opens, so
  // clicking Edit looked like nothing happened. Scroll it into view
  // whenever an edit actually starts.
  useEffect(() => {
    if (editing !== null) editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editing]);

  const dest = DESTINATIONS.find(d => d.value === destination);
  const isAnnouncement = dest.isAnnouncement;

  const load = useCallback(async () => {
    try {
      setError(null);
      if (isAnnouncement) {
        setContent(await adminHubAnnouncement.listAllAnnouncements());
      } else {
        setContent(await sequenceContent.listContent(destination));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load content.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, isAnnouncement]);

  useEffect(() => {
    setContent(null);
    setEditing(null);
    setImportResult(null);
    setBulkCreating(false);
    setBulkResult(null);
    load();
  }, [load]);

  const saveBulkRows = async (rows) => {
    setBulkSaving(true);
    setError(null);
    try {
      const result = isAnnouncement
        ? await adminHubAnnouncement.bulkCreateAnnouncements(rows)
        : await sequenceContent.bulkCreateContent(destination, rows);
      setBulkResult(result);
      setBulkCreating(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save these entries.');
    } finally {
      setBulkSaving(false);
    }
  };

  const save = async (fields) => {
    setSaving(true);
    try {
      if (isAnnouncement) {
        const annFields = {
          title: fields.title,
          message_text: fields.body,
          image_url: fields.image_url,
          image_alt_text: fields.image_alt_text,
          hub_url: (fields.resource_links || [])[0]?.url || null,
          day_number: fields.day_number,
        };
        if (editing === 'new') await adminHubAnnouncement.createAnnouncement(annFields);
        else await adminHubAnnouncement.updateAnnouncement(editing.id, annFields);
      } else {
        if (editing === 'new') await sequenceContent.createContent(destination, fields);
        else await sequenceContent.updateContent(editing.id, fields);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this entry.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewEmail = async (item) => {
    // Opened synchronously, before the fetch below — a browser's popup
    // blocker treats window.open() called after an await as NOT a direct
    // user action, even from a real click, and silently blocks it with no
    // error. Opening the blank tab first, then filling it in once the
    // content's ready, is the actual fix for that.
    const win = window.open('', '_blank');
    try {
      const html = await sequenceContent.getEmailPreviewHtml(item.id);
      if (win) { win.document.write(html); win.document.close(); }
    } catch (err) {
      if (win) win.close();
      setError(err.response?.data?.error || 'Could not load the email preview.');
    }
  };

  const removeOrDeactivate = async (item) => {
    if (isAnnouncement) {
      await adminHubAnnouncement.updateAnnouncement(item.id, { is_active: !item.is_active });
      await load();
      return;
    }
    if (!window.confirm(`Delete day ${item.day_number}${item.title ? ` — "${item.title}"` : ''}?`)) return;
    await sequenceContent.deleteContent(item.id);
    await load();
  };

  const deleteAnnouncementForGood = async (item) => {
    if (!window.confirm(`Permanently delete "${item.title || '(no title)'}"? This can't be undone.`)) return;
    await adminHubAnnouncement.deleteAnnouncement(item.id);
    await load();
  };

  const handleCombinedFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);
    setCombinedResult(null);
    setCombinedImporting(true);
    try {
      const csvText = await file.text();
      const result = await sequenceContent.bulkImportCombined(csvText);
      setCombinedResult(result);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed.');
    } finally {
      setCombinedImporting(false);
    }
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-choosing the same file later
    if (!file) return;

    setError(null);
    setImportResult(null);
    setImporting(true);
    try {
      const csvText = await file.text();
      const result = isAnnouncement
        ? await adminHubAnnouncement.bulkImportAnnouncements(csvText)
        : await sequenceContent.bulkImportContent(destination, csvText);
      setImportResult(result);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <AdminNav />

        <div className="mb-6">
          <h1 className="font-serif text-2xl font-bold text-reset-brown">Content Pushes</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Everything Team365 sends or posts to members — onboarding, daily, and renewal emails, and Hive announcements — in one place, one way of working.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
          <p className="text-sm font-semibold text-reset-brown mb-1">Upload your full content sheet</p>
          <p className="text-xs text-gray-500 mb-3">
            One spreadsheet, both Daily emails and Hub Announcements — each day's row is read for its Email Subject/Body columns (→ Daily) and its Hub Title/Body columns (→ Announcements) at the same time. Save your sheet as CSV first (File → Download → Comma Separated Values in Google Sheets).
          </p>
          <input ref={combinedFileInputRef} type="file" accept=".csv,text/csv" onChange={handleCombinedFileChosen} className="hidden" />
          <button
            onClick={() => combinedFileInputRef.current?.click()}
            disabled={combinedImporting}
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
          >
            {combinedImporting ? 'Importing…' : 'Upload combined sheet'}
          </button>
          {combinedResult && (
            <div className="mt-3 text-xs text-gray-600 bg-reset-cream rounded-lg p-3 space-y-0.5">
              <p>{combinedResult.totalRows} row(s) read.</p>
              <p>Daily: {combinedResult.daily.created} added, {combinedResult.daily.updated} updated, {combinedResult.daily.skipped} skipped (no Email Body Part 1).</p>
              <p>Announcements: {combinedResult.announcements.created} added{combinedResult.announcements.updated !== undefined ? `, ${combinedResult.announcements.updated} updated` : ''}, {combinedResult.announcements.skipped} skipped (no Hub Body).</p>
            </div>
          )}
          {combinedResult && <UploadCheck incomplete={combinedResult.incomplete} />}
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-xs font-medium w-fit mb-5 flex-wrap">
          {DESTINATIONS.map(d => (
            <button
              key={d.value}
              onClick={() => setDestination(d.value)}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                destination === d.value ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500 hover:text-reset-brown'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {destination === 'onboarding' && <NewMemberWelcomeSettings />}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-gray-500">
            {content ? `${content.length} ${dest.label.toLowerCase()} entr${content.length === 1 ? 'y' : 'ies'}` : '—'}
          </p>
          {editing === null && !bulkCreating && (
            <div className="flex gap-2">
              <button
                onClick={() => setBulkCreating(true)}
                className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors"
              >
                + Bulk create
              </button>
              <button
                onClick={() => setEditing('new')}
                className="px-4 py-2 border border-reset-terracotta text-reset-terracotta text-sm font-medium rounded-xl hover:bg-reset-cream transition-colors"
              >
                + One-off entry
              </button>
            </div>
          )}
        </div>

        {bulkResult && (
          <div className="mb-4 bg-reset-cream rounded-lg px-3 py-2 text-xs text-reset-brown">
            {bulkResult.created} added{bulkResult.updated !== undefined ? `, ${bulkResult.updated} updated` : ''}
            {bulkResult.skipped > 0 ? `, ${bulkResult.skipped} skipped (missing day number or content)` : ''}.
          </div>
        )}
        {bulkResult && <div className="mb-4 -mt-2"><UploadCheck incomplete={bulkResult.incomplete} /></div>}

        {isQuiz(destination) && <QuizEmailsPanel />}
        {destination === 'trial' && (
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-5 text-sm text-gray-600">
            <h2 className="font-semibold text-reset-brown mb-1">Trial emails</h2>
            For people who join with a trial code (never the founding members). Day 5, the day before their own trial ends,
            when their first real payment goes through, and a goodbye if their trial ends without joining. Write{' '}
            <span className="font-mono text-xs">{'{{first_name}}'}</span>, <span className="font-mono text-xs">{'{{date}}'}</span> (their trial end) and{' '}
            <span className="font-mono text-xs">{'{{price}}'}</span> (what they'll pay after, e.g. CA$20.58 a month). Keep the day numbers: 5, 1, 100 and 999.
          </div>
        )}

        {bulkCreating && (
          <BulkCreateTable
            destination={destination}
            isAnnouncement={isAnnouncement}
            saving={bulkSaving}
            onSave={saveBulkRows}
            onCancel={() => setBulkCreating(false)}
          />
        )}

        {editing !== null && (
          <div ref={editFormRef}>
            <ContentForm
              item={editing === 'new' ? null : editing}
              isAnnouncement={isAnnouncement}
              saving={saving}
              onSave={save}
              onCancel={() => setEditing(null)}
            />
          </div>
        )}

        {!content ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : content.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            No entries yet.
          </div>
        ) : (
          <div className="space-y-2">
            <TagLoader content={content} kind={isAnnouncement ? 'announcement' : 'push'} setItemTags={setItemTags} setAllTags={setAllTags} />
            {content.map(item => {
              const bodyText = isAnnouncement ? item.message_text : item.body;
              const linkCount = isAnnouncement ? (item.hub_url ? 1 : 0) : (item.resource_links?.length || 0);
              return (
                <React.Fragment key={item.id}>
                  <div className="bg-white rounded-2xl shadow-sm p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {!isAnnouncement && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-reset-cream text-reset-brown">
                            {dayLabel(destination, item.day_number)}
                          </span>
                        )}
                        {isAnnouncement && item.day_number != null && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-reset-cream text-reset-brown">
                            Linked to Day {item.day_number}
                          </span>
                        )}
                        <p className="text-sm font-semibold text-reset-brown truncate">{item.title || '(no title)'}</p>
                        {isAnnouncement && !item.is_active && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
                        )}
                        {item.image_url && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">image</span>
                        )}
                        {linkCount > 0 && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            {isAnnouncement ? 'has link' : `${linkCount} link${linkCount === 1 ? '' : 's'}`}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-2">{bodyText}</p>
                      <div className="mt-2">
                        <TagBubbles key={`${item.id}|${(itemTags[item.id] || []).join(',')}`} kind={isAnnouncement ? 'announcement' : 'push'} id={item.id}
                          initial={itemTags[item.id] || []} allTags={allTags}
                          onSaved={(tags) => {
                            setItemTags(m => ({ ...m, [item.id]: tags }));
                            api.get('/admin/tags', { params: { kind: isAnnouncement ? 'announcement' : 'push' } }).then(r => setAllTags(r.data.tags)).catch(() => {});
                          }} />
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <button
                        onClick={() => setPreviewingId(previewingId === item.id ? null : item.id)}
                        className="text-xs font-medium text-reset-sage hover:underline"
                      >
                        {previewingId === item.id ? 'Hide preview' : 'Preview'}
                      </button>
                      {!isAnnouncement && (
                        <>
                          <button
                            onClick={() => handlePreviewEmail(item)}
                            title="Opens the exact HTML that would be emailed, in a new tab"
                            className="text-xs font-medium text-gray-400 hover:text-reset-brown"
                          >
                            View as email →
                          </button>
                          <TestSendControl onSend={(email) => sequenceContent.sendTestEmail(item.id, email)} />
                        </>
                      )}
                      <button onClick={() => setEditing(item)} className="text-xs font-medium text-reset-terracotta hover:underline">
                        Edit
                      </button>
                      <button onClick={() => removeOrDeactivate(item)} className="text-xs font-medium text-red-400 hover:text-red-600">
                        {isAnnouncement ? (item.is_active ? 'Deactivate' : 'Reactivate') : 'Delete'}
                      </button>
                      {isAnnouncement && (
                        <button onClick={() => deleteAnnouncementForGood(item)} className="text-xs font-medium text-red-600 hover:underline">
                          Delete permanently
                        </button>
                      )}
                    </div>
                  </div>
                  {previewingId === item.id && <ContentPreview item={item} destination={destination} isAnnouncement={isAnnouncement} onClose={() => setPreviewingId(null)} />}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <div className="mt-8 pt-5 border-t border-gray-100">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-gray-400">
              Migrating old content from a spreadsheet export? Upload CSV columns: Fits with Days Content (day #), Title, Content, Image, Resources. An optional "Image Alt" column is picked up too.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleFileChosen} className="hidden" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="px-3 py-1.5 border border-gray-200 text-gray-500 text-xs font-medium rounded-lg hover:border-reset-sage hover:text-reset-sage transition-colors disabled:opacity-50"
              >
                {importing ? 'Importing…' : 'Upload CSV'}
              </button>
            </div>
          </div>
          {importResult && (
            <div className="mt-3 bg-reset-cream rounded-lg px-3 py-2 text-xs text-reset-brown">
              {importResult.created} added{importResult.updated !== undefined ? `, ${importResult.updated} updated` : ''}
              {importResult.skipped > 0 ? `, ${importResult.skipped} skipped (missing day number or content)` : ''}.
            </div>
          )}
          {importResult && <UploadCheck incomplete={importResult.incomplete} />}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ─── Send test email ─────────────────────────────────────────────────────
// A real send to a real inbox of Team365's choosing — same render, same
// Resend connection the live dispatch job uses — so what you see there is
// what a member's own email client actually does with it, not just what a
// browser tab renders. Fully separate from the live system: no member
// record involved, nothing toggled, nothing dispatched.
// Quiz follow-up emails (Jenn, 2026-10-13): people who take the free quiz on
// 365reset.com and ask for emails get these on days 2, 4, 6 and 8. In the
// words, {{first_name}} and {{overall_score}} are filled in for each person;
// the "See what's included" button, price, refund promise and stop link are
// added to every email automatically. Nothing sends while this is off.
const TIER_LABEL = { phoenix: 'Rising Phoenix', explorer: 'Evolving Explorer', glower: "Grounded Glow'er" };

function QuizEmailsPanel() {
  const [sending, setSending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [takers, setTakers] = useState(null);
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    sequenceContent.getQuizSettings().then(r => setSending(r.sending)).catch(() => setSending(null));
    sequenceContent.listQuizTakers().then(setTakers).catch(() => setTakers([]));
  }, []);

  const toggle = async () => {
    const next = !sending;
    if (next && !window.confirm('Turn quiz emails on? Everyone who asked for emails will start getting the next one in their series at 10am each morning.')) return;
    setBusy(true);
    try { setSending((await sequenceContent.setQuizSettings(next)).sending); } finally { setBusy(false); }
  };

  const wanting = (takers || []).filter(t => t.wantsEmails && !t.stoppedAt).length;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 mb-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-semibold text-reset-brown">Quiz follow-up emails</h2>
          <p className="text-sm text-gray-600 mt-1">
            People who take the quiz on 365reset.com and ask for emails get their results right away, then these on days 2, 4, 6 and 8.
            Write <span className="font-mono text-xs">{'{{first_name}}'}</span> for their first name and <span className="font-mono text-xs">{'{{overall_score}}'}</span> for their score.
            The "See what's included" button, the price, the refund promise and the stop link are added to every email for you.
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy || sending === null}
          className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${sending ? 'bg-reset-sage text-white' : 'border border-gray-300 text-gray-600 hover:bg-reset-cream'}`}
        >
          {sending === null ? 'Loading…' : sending ? 'Sending is ON — turn off' : 'Sending is off — turn on'}
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-3">
        {takers === null ? 'Loading who took the quiz…' : `${takers.length} ${takers.length === 1 ? 'person has' : 'people have'} taken the quiz · ${wanting} asked for emails.`}{' '}
        {takers?.length > 0 && (
          <button type="button" onClick={() => setShowList(v => !v)} className="font-medium text-reset-terracotta hover:text-reset-brown">
            {showList ? 'Hide list' : 'See who'}
          </button>
        )}
      </p>
      {showList && takers?.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500">
              <th className="py-1 pr-3 font-medium">Name</th><th className="py-1 pr-3 font-medium">Email</th>
              <th className="py-1 pr-3 font-medium">Result</th><th className="py-1 pr-3 font-medium">Emails</th><th className="py-1 font-medium">Took it</th>
            </tr></thead>
            <tbody>
              {takers.map(t => (
                <tr key={t.id} className="border-t border-gray-100">
                  <td className="py-1.5 pr-3">{t.firstName}</td>
                  <td className="py-1.5 pr-3 break-all">{t.email}</td>
                  <td className="py-1.5 pr-3">{TIER_LABEL[t.tier] || t.tier} · {t.score}</td>
                  <td className="py-1.5 pr-3">
                    {!t.wantsEmails ? 'Didn\'t ask' : t.stoppedAt ? 'Said stop' : t.emailsSent.length ? `Sent: ${t.emailsSent.map(d => d === 1 ? 'results' : `day ${d}`).join(', ')}` : 'None yet'}
                  </td>
                  <td className="py-1.5">{new Date(t.takenAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TestSendControl({ onSend }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem('sequenceContentTestEmail') || '');
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | <error string>
  const inputRef = useRef(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const send = async () => {
    const trimmed = email.trim();
    if (!trimmed || status === 'sending') return;
    setStatus('sending');
    try {
      await onSend(trimmed);
      localStorage.setItem('sequenceContentTestEmail', trimmed);
      setStatus('sent');
    } catch (err) {
      setStatus(err.response?.data?.error || 'Could not send.');
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setStatus(null); }}
        className="text-xs font-medium text-gray-400 hover:text-reset-brown"
      >
        Send test email →
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        ref={inputRef}
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') send(); if (e.key === 'Escape') setOpen(false); }}
        placeholder="you@email.com"
        className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-40 focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
      />
      <button
        type="button" onClick={send} disabled={status === 'sending' || !email.trim()}
        className="text-xs font-medium text-reset-terracotta hover:underline disabled:opacity-50"
      >
        {status === 'sending' ? 'Sending…' : 'Send'}
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-400 hover:text-red-500">✕</button>
      {status === 'sent' && <span className="text-xs text-reset-sage">Sent — check the inbox</span>}
      {status && status !== 'sending' && status !== 'sent' && <span className="text-xs text-red-500">{status}</span>}
    </div>
  );
}

// ─── Preview ─────────────────────────────────────────────────────────────
// For sequence content, the real email-sending engine isn't built yet, so
// this isn't a literal email preview — there's no template to match. It's
// a "laid out, not raw spreadsheet text" view of the content itself. For
// announcements, this mirrors the real member-facing AnnouncementDetail
// page layout exactly, since that page already exists and is live.

function ContentPreview({ item, destination, isAnnouncement, onClose }) {
  const bodyText = isAnnouncement ? item.message_text : item.body;
  const title = item.title || (isAnnouncement ? 'Announcement' : `Day ${item.day_number}`);
  const dateLabel = isAnnouncement && item.created_at
    ? new Date(item.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="border-2 border-dashed border-reset-sage rounded-2xl p-3">
      <p className="text-[10px] font-semibold text-reset-sage uppercase tracking-wide mb-2 flex items-center justify-between">
        {isAnnouncement ? 'Preview — what a member sees' : 'Content preview — not the final email layout, just laid out for review'}
        <button onClick={onClose} className="text-gray-400 hover:text-reset-brown font-normal normal-case">Close</button>
      </p>
      <article className="bg-white rounded-2xl shadow-sm overflow-hidden max-w-2xl">
        {item.image_url && (
          <img src={item.image_url} alt={item.image_alt_text || ''} className="w-full aspect-square object-cover" />
        )}
        <div className="p-6">
          {dateLabel && <p className="text-xs text-gray-400 mb-2">{dateLabel}</p>}
          {!isAnnouncement && <p className="text-xs text-gray-400 mb-2">{dayLabel(destination, item.day_number)}</p>}
          <h2 className="font-serif text-2xl font-bold text-reset-brown mb-4">{title}</h2>
          <div className="text-sm text-gray-700 leading-relaxed">
            {isAnnouncement ? renderRichText(bodyText) : renderRichText(bodyText)}
          </div>
          {isAnnouncement ? (
            item.hub_url && (
              <span className="inline-block mt-4 text-sm font-medium text-reset-terracotta">Quick link to the Hub →</span>
            )
          ) : (
            item.resource_links?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {item.resource_links.map((l, i) => (
                  <span key={i} className="inline-block text-sm font-medium text-reset-terracotta">
                    {l.title || l.url} →
                  </span>
                ))}
              </div>
            )
          )}
        </div>
      </article>
    </div>
  );
}

// ─── One-off create/edit form ──────────────────────────────────────────────

function ContentForm({ item, isAnnouncement, saving, onSave, onCancel }) {
  const [dayNumber, setDayNumber] = useState(item?.day_number ?? '');
  const [title, setTitle] = useState(item?.title || '');
  const [body, setBody] = useState(isAnnouncement ? (item?.message_text || '') : (item?.body || ''));
  const [bodyPart2, setBodyPart2] = useState(item?.body_part2 || '');
  const [imageUrl, setImageUrl] = useState(item?.image_url || '');
  const [imageAltText, setImageAltText] = useState(item?.image_alt_text || '');
  const [resourceLinksText, setResourceLinksText] = useState(
    isAnnouncement ? (item?.hub_url || '') : linksToText(item?.resource_links)
  );

  // Auto-fills alt text for a picture that's already been described
  // somewhere else, so it's typed once per image, not once per use.
  const handleImageUrlBlur = async () => {
    const url = imageUrl.trim();
    if (!url || imageAltText.trim()) return;
    try {
      const saved = await sequenceContent.getSavedImageAltText(url);
      if (saved) setImageAltText(saved);
    } catch { /* not critical — she can still type it herself */ }
  };

  // Red warning before Save, live as she types — shown once anything's been
  // entered, so a brand-new blank form doesn't open already shouting.
  const hasAnything = [dayNumber, title, body, bodyPart2, imageUrl].some(v => String(v ?? '').trim());
  const gaps = hasAnything ? problemsFor({ isAnnouncement, title, body, imageUrl, imageAltText }) : [];
  // Still savable with these gaps (no text, or an announcement with no title, can't be saved at all).
  const savableWithGaps = gaps.length > 0 && body.trim() && (!isAnnouncement || title.trim());

  const submit = (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    if (!isAnnouncement && !dayNumber) return;

    if (isAnnouncement) {
      if (!title.trim()) return;
      onSave({
        title: title.trim(),
        body: body.trim(),
        image_url: imageUrl.trim() || null,
        image_alt_text: imageAltText.trim() || null,
        resource_links: resourceLinksText.trim() ? [{ title: '', url: resourceLinksText.trim() }] : [],
        day_number: dayNumber ? Number(dayNumber) : null,
      });
      return;
    }

    onSave({
      dayNumber: Number(dayNumber), day_number: Number(dayNumber),
      title: title.trim() || null,
      body: body.trim(),
      bodyPart2: bodyPart2.trim() || null, body_part2: bodyPart2.trim() || null,
      imageUrl: imageUrl.trim() || null, image_url: imageUrl.trim() || null,
      imageAltText: imageAltText.trim() || null, image_alt_text: imageAltText.trim() || null,
      resourceLinks: textToLinks(resourceLinksText), resource_links: textToLinks(resourceLinksText),
    });
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 space-y-3 mb-4">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Day number{isAnnouncement ? ' (optional — links this to that day\'s daily email; leave blank for a general Hive post)' : ''}
        </label>
        <input
          type="number" min="1" value={dayNumber} onChange={e => setDayNumber(e.target.value)} required={!isAnnouncement}
          className="w-32 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Title{isAnnouncement ? '' : ' (optional)'}</label>
        <input
          value={title} onChange={e => setTitle(e.target.value)} required={isAnnouncement}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Content</label>
        <RichTextEditor value={body} onChange={setBody} minHeightClass={isAnnouncement ? 'min-h-[120px]' : 'min-h-[200px]'} />
      </div>
      {!isAnnouncement && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Content — after the image (optional)</label>
          <RichTextEditor value={bodyPart2} onChange={setBodyPart2} minHeightClass="min-h-[100px]" />
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Image URL (optional — Google Drive share links work)</label>
        <input
          value={imageUrl} onChange={e => setImageUrl(e.target.value)} onBlur={handleImageUrlBlur}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      {imageUrl.trim() && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Image alt text (describe the image for screen readers — remembered per picture, reused automatically)</label>
          <input
            value={imageAltText} onChange={e => setImageAltText(e.target.value)}
            placeholder="e.g. Jenn stretching outdoors at sunrise"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          {isAnnouncement ? 'External link (optional — one only, shown as a "Quick link")' : 'Resource links (optional — one per line, "Label | URL")'}
        </label>
        {isAnnouncement ? (
          <input
            value={resourceLinksText} onChange={e => setResourceLinksText(e.target.value)}
            placeholder="https://..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        ) : (
          <textarea
            value={resourceLinksText} onChange={e => setResourceLinksText(e.target.value)} rows={2}
            placeholder="Worksheet | https://..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        )}
      </div>
      {gaps.length > 0 && (
        <div role="alert" className="bg-red-50 border-2 border-red-300 text-red-800 rounded-xl px-4 py-3 text-sm">
          <span className="font-semibold">Before you save, this entry has:</span> {gaps.join(', ')}.
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">
          Cancel
        </button>
        <button
          type="submit" disabled={saving}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : savableWithGaps ? 'Save anyway' : 'Save'}
        </button>
      </div>
    </form>
  );
}

// ─── Bulk create — write several days' worth of content natively, right here,
// in one sitting. No file, no external tool: this is the direct replacement
// for building content in a spreadsheet and uploading it. ─────────────────

const blankBulkRow = () => ({ key: Math.random().toString(36).slice(2), dayNumber: '', title: '', body: '', bodyPart2: '', imageUrl: '', imageAltText: '', resourceLinksText: '' });

function BulkCreateTable({ destination, isAnnouncement, saving, onSave, onCancel }) {
  const [rows, setRows] = useState([blankBulkRow(), blankBulkRow(), blankBulkRow()]);
  const [pasteNotice, setPasteNotice] = useState(null);
  const [pasteLeftOut, setPasteLeftOut] = useState([]);

  // What's empty on each row, before Save. A row that can't be saved at all
  // says so rather than being silently dropped from the save; untouched
  // blank rows say nothing.
  const rowProblems = (r) => {
    if (![r.dayNumber, r.title, r.body, r.bodyPart2, r.imageUrl].some(v => (v || '').trim())) return [];
    if (!r.body.trim()) return ['NOT SAVED — ' + (isAnnouncement ? 'no message' : 'no email text')];
    if (!isAnnouncement && !r.dayNumber) return ['NOT SAVED — no day number'];
    return problemsFor({ isAnnouncement, title: r.title, body: r.body, imageUrl: r.imageUrl, imageAltText: r.imageAltText });
  };
  const draftGaps = rows
    .map((r, i) => ({ kind: isAnnouncement ? 'announcement' : 'daily', day: r.dayNumber || null, label: `Row ${i + 1}${r.dayNumber ? ` (Day ${r.dayNumber})` : ''}`, problems: rowProblems(r) }))
    .filter(g => g.problems.length > 0);

  const updateRow = (key, field, value) => {
    setRows(rs => rs.map(r => (r.key === key ? { ...r, [field]: value } : r)));
  };
  const removeRow = (key) => setRows(rs => rs.filter(r => r.key !== key));
  const addRow = () => setRows(rs => [...rs, blankBulkRow()]);

  // Auto-fills alt text for a picture that's already been described
  // somewhere else, so it's typed once per image, not once per use.
  const handleRowImageUrlBlur = async (row) => {
    const url = row.imageUrl.trim();
    if (!url || row.imageAltText.trim()) return;
    try {
      const saved = await sequenceContent.getSavedImageAltText(url);
      if (saved) updateRow(row.key, 'imageAltText', saved);
    } catch { /* not critical — she can still type it herself */ }
  };

  const handlePreviewDraftRow = async (row) => {
    const win = window.open('', '_blank'); // opened synchronously — see handlePreviewEmail's note
    try {
      const html = await sequenceContent.getDraftEmailPreviewHtml({
        title: row.title.trim() || null,
        body: row.body.trim(),
        bodyPart2: (row.bodyPart2 || '').trim() || null,
        imageUrl: row.imageUrl.trim() || null,
        imageAltText: row.imageAltText.trim() || null,
        resourceLinks: textToLinks(row.resourceLinksText),
        dayNumber: row.dayNumber.trim() || null,
        sequenceType: destination,
      });
      if (win) { win.document.write(html); win.document.close(); }
    } catch {
      if (win) win.close();
    }
  };

  const handlePasteGrid = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const grid = html ? (parseHtmlGrid(html) || parsePlainGrid(text)) : parsePlainGrid(text);
    const { rows: pastedRows, leftOut, notice } = gridToBulkRows(grid, isAnnouncement);
    setPasteNotice(notice);
    setPasteLeftOut(leftOut);
    if (pastedRows.length === 0) return;
    setRows(rs => {
      const untouched = rs.filter(r => r.dayNumber.trim() || r.title.trim() || r.body.trim());
      return [...untouched, ...pastedRows];
    });
    // The paste doesn't fire a blur event, so check each pasted row's
    // picture for already-saved alt text directly.
    pastedRows.forEach(row => { if (row.imageUrl && !row.imageAltText) handleRowImageUrlBlur(row); });
  };

  const submit = () => {
    const payload = rows
      .filter(r => (isAnnouncement || r.dayNumber) && r.body.trim())
      .map(r => ({
        dayNumber: r.dayNumber ? Number(r.dayNumber) : null,
        title: r.title.trim() || null,
        body: r.body.trim(),
        bodyPart2: (r.bodyPart2 || '').trim() || null,
        imageUrl: r.imageUrl.trim() || null,
        imageAltText: (r.imageAltText || '').trim() || null,
        resourceLinks: textToLinks(r.resourceLinksText),
      }));
    if (payload.length === 0) return;
    onSave(payload);
  };

  const readyCount = rows.filter(r => (isAnnouncement || r.dayNumber) && r.body.trim()).length;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 mb-4 space-y-5">
      <div>
        <p className="text-sm font-semibold text-reset-brown">Bulk create</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Write as many as you want below, one at a time — or select any block of rows in Google Sheets or Excel and paste it below. Include your heading row (Day, Email Subject, Hub Title…) and every column lands by its name; without it, the columns are worked out from what's in them. Bold and italic from the sheet carry over.
          {isAnnouncement && ' Day here only decides the posting order — the highest day ends up as the most recent post.'}
        </p>
      </div>

      <textarea
        onPaste={handlePasteGrid}
        value=""
        onChange={() => {}}
        placeholder="Click here, then paste (Cmd/Ctrl+V) a range copied from your spreadsheet…"
        rows={2}
        className="w-full border-2 border-dashed border-reset-sage/50 rounded-xl px-3 py-3 text-sm text-center text-gray-400 focus:outline-none focus:ring-2 focus:ring-reset-sage focus:border-solid resize-none"
      />
      {pasteNotice && (
        <div role="alert" className="-mt-2 bg-red-50 border-2 border-red-300 text-red-800 rounded-xl px-4 py-3 text-sm font-medium">{pasteNotice}</div>
      )}
      {pasteLeftOut.length > 0 && (
        <div className="-mt-5">
          <IncompleteList
            items={pasteLeftOut}
            heading={`${pasteLeftOut.length} pasted ${pasteLeftOut.length === 1 ? 'row was' : 'rows were'} left out:`}
          />
        </div>
      )}

      {rows.map((row, i) => (
        <div key={row.key} className="border border-gray-100 rounded-xl p-4 space-y-2.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-end gap-3 flex-wrap flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Day</label>
                <input
                  type="number" min="1" value={row.dayNumber}
                  onChange={e => updateRow(row.key, 'dayNumber', e.target.value)}
                  className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                <input
                  value={row.title} onChange={e => updateRow(row.key, 'title', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                />
              </div>
            </div>
            {rows.length > 1 && (
              <button type="button" onClick={() => removeRow(row.key)} className="text-xs text-red-400 hover:text-red-600 shrink-0 mt-5">
                Remove
              </button>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Content</label>
            <RichTextEditor value={row.body} onChange={v => updateRow(row.key, 'body', v)} minHeightClass="min-h-[90px]" />
          </div>

          {!isAnnouncement && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Content — after the image (optional)</label>
              <RichTextEditor value={row.bodyPart2} onChange={v => updateRow(row.key, 'bodyPart2', v)} minHeightClass="min-h-[60px]" />
            </div>
          )}

          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Image URL (optional)</label>
              <input
                value={row.imageUrl} onChange={e => updateRow(row.key, 'imageUrl', e.target.value)}
                onBlur={() => handleRowImageUrlBlur(row)}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
            </div>
            {row.imageUrl.trim() && (
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Image alt text</label>
                <input
                  value={row.imageAltText} onChange={e => updateRow(row.key, 'imageAltText', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                />
              </div>
            )}
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {isAnnouncement ? 'External link (optional — one only)' : 'Resource links (optional — one per line, "Label | URL")'}
              </label>
              <textarea
                value={row.resourceLinksText} onChange={e => updateRow(row.key, 'resourceLinksText', e.target.value)}
                rows={2}
                placeholder="Replay | https://..."
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
            </div>
          </div>
          {rowProblems(row).length > 0 && (
            <p className="text-xs font-medium text-red-700 bg-red-50 rounded-lg px-2 py-1">Empty: {rowProblems(row).join(', ')}</p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-gray-400">Row {i + 1}</p>
            {!isAnnouncement && row.body.trim() && (
              <div className="flex items-center gap-3">
                <button
                  type="button" onClick={() => handlePreviewDraftRow(row)}
                  className="text-xs font-medium text-gray-400 hover:text-reset-brown"
                >
                  View as email →
                </button>
                <TestSendControl onSend={(email) => sequenceContent.sendTestEmailDraft({
                  email,
                  title: row.title.trim() || null,
                  body: row.body.trim(),
                  bodyPart2: (row.bodyPart2 || '').trim() || null,
                  imageUrl: row.imageUrl.trim() || null,
                  imageAltText: row.imageAltText.trim() || null,
                  resourceLinks: textToLinks(row.resourceLinksText),
                  dayNumber: row.dayNumber.trim() || null,
                  sequenceType: destination,
                })} />
              </div>
            )}
          </div>
        </div>
      ))}

      <button
        type="button" onClick={addRow}
        className="text-sm font-medium text-reset-terracotta hover:underline"
      >
        + Add another
      </button>

      <IncompleteList items={draftGaps} heading="Before you save, please check:" />

      <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">
          Cancel
        </button>
        <button
          type="button" onClick={submit} disabled={saving || readyCount === 0}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : `${draftGaps.length > 0 ? 'Save anyway:' : 'Save'} ${readyCount || ''} ${readyCount === 1 ? 'entry' : 'entries'}`.trim()}
        </button>
      </div>
    </div>
  );
}
